import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../auth/SupabaseClient";
import {
  PlusCircle,
  Edit,
  Trash2,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface Category {
  id: string;
  name: string;
  description: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

interface CategoriesManagerProps {
  darkMode: boolean;
}

export default function CategoriesManager({
  darkMode,
}: CategoriesManagerProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof Category>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order(sortField, { ascending: sortDirection === "asc" });

      if (error) throw error;
      setCategories(data || []);
    } catch (error: any) {
      console.error("Error fetching categories:", error);
      setError(
        `Une erreur est survenue lors de la récupération des catégories: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEditingCategory((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleAddCategory = async () => {
    if (!editingCategory || !editingCategory.name) {
      alert("Le nom de la catégorie est requis.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("categories")
        .insert([
          {
            name: editingCategory.name,
            description: editingCategory.description,
          },
        ])
        .select();

      if (error) throw error;

      setCategories([...categories, ...(data || [])]);
      setEditingCategory(null);
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error adding category:", error);
      setError(
        `Une erreur est survenue lors de l'ajout de la catégorie: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory || !editingCategory.name) {
      alert("Le nom de la catégorie est requis.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("categories")
        .update({
          name: editingCategory.name,
          description: editingCategory.description,
        })
        .eq("id", editingCategory.id)
        .select();

      if (error) throw error;

      setCategories(
        categories.map((cat) =>
          cat.id === editingCategory.id && data?.[0]
            ? (data[0] as Category)
            : cat
        )
      );
      setEditingCategory(null);
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error updating category:", error);
      setError(
        `Une erreur est survenue lors de la modification de la catégorie: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette catégorie ?"))
      return;

    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase.from("categories").delete().eq("id", id);

      if (error) throw error;

      setCategories(categories.filter((cat) => cat.id !== id));
    } catch (error: any) {
      console.error("Error deleting category:", error);
      setError(
        `Une erreur est survenue lors de la suppression de la catégorie: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditingCategory(null);
    setIsEditing(false);
  };

  const handleSort = (field: keyof Category) => {
    setSortDirection(
      field === sortField && sortDirection === "asc" ? "desc" : "asc"
    );
    setSortField(field);
  };

  const filteredCategories = categories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div
          className={`animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 ${
            darkMode ? "border-blue-400" : "border-blue-500"
          }`}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`text-red-500 text-center p-4 ${
          darkMode ? "bg-red-900" : "bg-red-100"
        } rounded-lg m-4`}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className={`container mx-auto p-6 ${
        darkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"
      }`}
    >
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Catégories</h1>
        {!isEditing && (
          <button
            onClick={() => {
              setEditingCategory({
                id: "",
                name: "",
                description: "",
                owner_id: "",
                created_at: "",
                updated_at: "",
              });
              setIsEditing(true);
            }}
            className={`px-4 py-2 rounded-lg flex items-center ${
              darkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            Ajouter une catégorie
          </button>
        )}
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
              darkMode ? "text-gray-400" : "text-gray-500"
            }`}
          />
          <input
            type="text"
            placeholder="Rechercher..."
            className={`w-full pl-10 pr-4 py-2 rounded-lg ${
              darkMode ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-800"
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isEditing && (
        <div
          className={`${
            darkMode ? "bg-gray-800" : "bg-white"
          } shadow-md rounded-lg p-6 mb-6`}
        >
          <h2 className="text-xl font-semibold mb-4">
            {editingCategory?.id
              ? "Modifier la Catégorie"
              : "Ajouter une Nouvelle Catégorie"}
          </h2>
          <div className="space-y-4">
            <input
              type="text"
              name="name"
              placeholder="Nom de la catégorie"
              value={editingCategory?.name || ""}
              onChange={handleInputChange}
              className={`w-full p-2 border rounded-md ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-800"
              }`}
            />
            <textarea
              name="description"
              placeholder="Description"
              value={editingCategory?.description || ""}
              onChange={handleInputChange}
              className={`w-full p-2 border rounded-md ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-gray-800"
              }`}
              rows={3}
            />
            <div className="flex space-x-4">
              <button
                onClick={
                  editingCategory?.id ? handleEditCategory : handleAddCategory
                }
                className={`px-4 py-2 rounded-md ${
                  darkMode
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
              >
                {editingCategory?.id ? "Modifier" : "Ajouter"}
              </button>
              <button
                onClick={handleCancel}
                className={`px-4 py-2 rounded-md ${
                  darkMode
                    ? "bg-gray-600 hover:bg-gray-700 text-white"
                    : "bg-gray-500 hover:bg-gray-600 text-white"
                }`}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-grow">
        <div
          className={`overflow-x-auto overflow-y-auto max-h-[calc(100vh-600px)] min-h-[calc(100vh-350px)] rounded-lg border ${
            darkMode ? "border-gray-600" : "border-gray-300"
          }`}
        >
          <div className="inline-block min-w-full align-middle">
            <table
              className={`min-w-full rounded-lg ${
                darkMode ? "bg-gray-700" : "bg-white"
              }`}
            >
              <thead
                className={`${
                  darkMode ? "bg-gray-800" : "bg-gray-100"
                } sticky top-0 z-10`}
              >
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider cursor-pointer`}
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center">
                      Nom
                      {sortField === "name" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider cursor-pointer`}
                    onClick={() => handleSort("description")}
                  >
                    <div className="flex items-center">
                      Description
                      {sortField === "description" &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody
                className={`${darkMode ? "bg-gray-900" : "bg-white"} divide-y ${
                  darkMode ? "divide-gray-700" : "divide-gray-200"
                }`}
              >
                {filteredCategories.map((category) => (
                  <tr
                    key={category.id}
                    className={
                      darkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                    }
                  >
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        darkMode ? "text-gray-300" : "text-gray-900"
                      }`}
                    >
                      {category.name}
                    </td>
                    <td
                      className={`px-6 py-4 whitespace-nowrap ${
                        darkMode ? "text-gray-300" : "text-gray-500"
                      }`}
                    >
                      {category.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setEditingCategory(category);
                          setIsEditing(true);
                        }}
                        className={`text-blue-600 hover:text-blue-900 ${
                          darkMode ? "hover:text-blue-400" : ""
                        } mr-4`}
                      >
                        <Edit className="w-5 h-5 inline" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className={`text-red-600 hover:text-red-900 ${
                          darkMode ? "hover:text-red-400" : ""
                        }`}
                      >
                        <Trash2 className="w-5 h-5 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
